import React, { useState, useRef, useEffect } from "react";
import { Building2, PlusCircle } from "lucide-react";
import INDIAN_COLLEGES from "../data/colleges";

export default function CollegeSearch({ value, onChange, required, focusColor = "blue" }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const wrapperRef = useRef(null);

  // Sync external value
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        // Auto-confirm typed text when clicking outside if no suggestion was picked
        if (query.trim().length >= 2 && !value) {
          onChange(query.trim());
          setIsCustom(true);
        }
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [query, value, onChange]);

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    setIsCustom(false);
    onChange("");  // clear confirmed value while typing

    if (val.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const lower = val.toLowerCase();
    const matches = INDIAN_COLLEGES.filter(c =>
      c.toLowerCase().includes(lower)
    ).slice(0, 10);
    setSuggestions(matches);
    setOpen(true);
  }

  function handleSelect(college, custom = false) {
    setQuery(college);
    onChange(college);
    setIsCustom(custom);
    setSuggestions([]);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && query.trim().length >= 2 && suggestions.length === 0) {
      e.preventDefault();
      handleSelect(query.trim(), true);
    }
  }

  function handleFocus() {
    if (query.length >= 2) {
      setOpen(true);
    }
  }

  const ring = focusColor === "amber"
    ? "focus-within:ring-amber-500"
    : "focus-within:ring-blue-500";

  return (
    <div className="relative" ref={wrapperRef}>
      <div className={`relative flex items-center border rounded-xl focus-within:ring-2 ${ring} focus-within:border-transparent ${isCustom ? "border-green-400 bg-green-50" : "border-gray-200"}`}>
        <Building2 size={16} className={`absolute left-3 pointer-events-none ${isCustom ? "text-green-500" : "text-gray-400"}`} />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder="Search your college..."
          required={required}
          autoComplete="off"
          className={`w-full pl-10 pr-4 py-3 text-sm rounded-xl outline-none bg-transparent`}
        />
        {isCustom && (
          <span className="absolute right-3 text-xs text-green-600 font-medium whitespace-nowrap">Custom</span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-56 overflow-y-auto">
          {suggestions.map((college) => (
            <li
              key={college}
              onMouseDown={() => handleSelect(college)}
              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer first:rounded-t-xl last:rounded-b-xl"
            >
              {college}
            </li>
          ))}
        </ul>
      )}

      {open && query.trim().length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1">
          <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100">
            No college found matching "{query}"
          </div>
          <button
            type="button"
            onMouseDown={() => handleSelect(query.trim(), true)}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-blue-700 hover:bg-blue-50 rounded-b-xl font-medium"
          >
            <PlusCircle size={15} className="text-blue-500 shrink-0" />
            Add "{query}" as my college
          </button>
        </div>
      )}

      {isCustom && (
        <p className="mt-1 text-xs text-green-600 pl-1">
          Custom college name added. You can edit it anytime.
        </p>
      )}
    </div>
  );
}
